import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import RichTextEditor from "@/components/RichTextEditor";

const BLANK = {
  slug: "", title: "", excerpt: "", content_html: "", cover_image: "",
  tags: [], published: false, read_minutes: 3,
};

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/blog/admin").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setDraft(BLANK); setEditingId(null); setOpen(true); };
  const openEdit = (p) => { setDraft({ ...p, tags: p.tags || [] }); setEditingId(p.id); setOpen(true); };

  const save = async () => {
    const payload = { ...draft, slug: draft.slug || slugify(draft.title) };
    try {
      if (editingId) await api.put(`/blog/${editingId}`, payload);
      else await api.post("/blog", payload);
      setOpen(false);
      toast.success("Saved.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/blog/${id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePublish = async (p) => {
    try {
      await api.put(`/blog/${p.id}`, { published: !p.published });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <header className="mb-8 flex items-baseline justify-between">
        <div>
          <p className="label-xs">Blog</p>
          <h1 className="mt-2 font-heading font-black text-4xl tracking-tighter">Posts</h1>
        </div>
        <Button onClick={openNew} data-testid="blog-new"><Plus className="h-4 w-4" /> New post</Button>
      </header>

      <div className="border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left label-xs">
              <th className="p-3">Title</th>
              <th className="p-3">Slug</th>
              <th className="p-3 w-28">Status</th>
              <th className="p-3">Tags</th>
              <th className="p-3 w-40"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-border">
                <td className="p-3 font-medium cursor-pointer" onClick={() => openEdit(p)} data-testid={`blog-open-${p.slug}`}>{p.title}</td>
                <td className="p-3 font-mono text-xs text-muted-foreground">/{p.slug}</td>
                <td className="p-3">
                  <button onClick={() => togglePublish(p)} className={`text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-1 border ${p.published ? "border-[var(--brand-accent)] text-[var(--brand-accent)]" : "border-border text-muted-foreground"}`}>
                    {p.published ? "Published" : "Draft"}
                  </button>
                </td>
                <td className="p-3 text-xs text-muted-foreground">{p.tags?.join(", ")}</td>
                <td className="p-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>Edit</Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(p.id)}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="label-xs">Title</Label>
                <Input className="mt-2" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value, slug: draft.slug || slugify(e.target.value) })} data-testid="blog-title" />
              </div>
              <div>
                <Label className="label-xs">Slug</Label>
                <Input className="mt-2" value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: slugify(e.target.value) })} />
              </div>
            </div>
            <div>
              <Label className="label-xs">Excerpt</Label>
              <Textarea className="mt-2" rows={2} value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="label-xs">Cover image URL</Label>
                <Input className="mt-2" value={draft.cover_image ?? ""} onChange={(e) => setDraft({ ...draft, cover_image: e.target.value })} />
              </div>
              <div>
                <Label className="label-xs">Read minutes</Label>
                <Input type="number" className="mt-2" value={draft.read_minutes} onChange={(e) => setDraft({ ...draft, read_minutes: Number(e.target.value) })} />
              </div>
            </div>
            <div>
              <Label className="label-xs">Tags (comma-separated)</Label>
              <Input className="mt-2" value={draft.tags.join(", ")} onChange={(e) => setDraft({ ...draft, tags: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            </div>
            <div>
              <Label className="label-xs">Content</Label>
              <div className="mt-2">
                <RichTextEditor value={draft.content_html} onChange={(html) => setDraft({ ...draft, content_html: html })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!draft.published} onChange={(e) => setDraft({ ...draft, published: e.target.checked })} data-testid="blog-published" /> Publish
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="blog-save"><Save className="h-4 w-4" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
